import { CheckIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';

import { PAGE_TYPES } from '@dashboard/enums/page-types';

import { PaymentsChargesList } from '@dashboard/scenes/PaymentsCharges';
import { PaymentsSubscriptionsList } from '@dashboard/scenes/PaymentsSubscriptions';
import { SubmissionsList } from '@dashboard/scenes/Submissions';

import Button from '@dashboard/components/Button';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';

import PageEditView from '../components/PageEditView';
import TestCard from '../components/TestCard';
import TestModeScope from '../components/TestModeScope';
import { usePreview } from '@/dashboard/context/PreviewContext';
import getEventsAppUrl from '@dashboard/lib/getEventsAppUrl';
import { hasOneTimePrice, hasRecurringPrice } from '@dashboard/services/getBillingModel';

const PageTest = ({ page }) => {
  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  useEffect(() => {
    setPreview({
      type: 'test-checkout',
    });

    return () => {
      setPreview({
        type: 'checkout',
      });
    };
  }, []);

  return (
    <PageEditView
      tabs={[
        {
          ...(page.type === PAGE_TYPES.CHECKOUT && {
            path: '/payments',
            title: 'Test payments',
            label: 'Test payments',
            component: (
              <>
                <TestModeScope />
                {hasOneTimePrice(page.product) && <TestPayments page={page} />}
                {hasRecurringPrice(page.product) && (
                  <TestSubscriptions page={page} hideTestCard={hasOneTimePrice(page.product)} />
                )}
              </>
            ),
            description: 'Use the test card below to simulate a payment.',
            helpUrl: '/docs/articles/how-to-make-test-payments',
          }),
          ...(page.type === 'form' && {
            path: '/payments',
            title: 'Test submissions',
            label: 'Test submissions',
            component: (
              <>
                <TestModeScope />
                <TestSubmissions page={page} />
              </>
            ),
            description: 'Submit a test response to see how your form works.',
          }),
          ...(page.type === PAGE_TYPES.EVENT && {
            path: '/payments',
            title: 'Test payments',
            label: 'Test payments',
            component: (
              <>
                <TestModeScope />
                <TestPayments page={page} />
              </>
            ),
            description: 'Use the test card below to simulate a payment.',
            helpUrl: '/docs/articles/how-to-make-test-payments',
          }),
        },
      ]}
    />
  );
};

function TestSubmissions({ page }) {
  const [refreshKey, setRefreshKey] = useState(0);

  // listen to postmessage checkout event
  React.useEffect(() => {
    const handleCheckoutEvent = event => {
      if (event.data?.type === 'checkoutpage.event') {
        if (event.data.payload.event === 'submission') {
          setRefreshKey(refreshKey + 1);
        }
      }
    };

    window.addEventListener('message', handleCheckoutEvent);

    return () => {
      window.removeEventListener('message', handleCheckoutEvent);
    };
  }, [refreshKey]);

  return (
    <div className="w-full">
      <CardHeaderTitle variant="root" className="mb-4">
        Test submissions
      </CardHeaderTitle>

      <div className="overflow-auto mb-4">
        <SubmissionsList
          options={{
            hidePageName: true,
            hideStatus: true,
            appearance: 'compact',
          }}
          sort={{ createdAt: -1 }}
          filter={{ livemode: false, page: page._id }}
          refreshKey={refreshKey}
          emptyStateSubtitle="Test submissions will be shown here."
        />
      </div>
    </div>
  );
}

function TestPayments({ page }) {
  const [refreshKey, setRefreshKey] = useState(0);

  const eventAppHref = getEventsAppUrl(page._id, 'guest-list', { livemode: false });

  // listen to postmessage checkout event
  React.useEffect(() => {
    const handleCheckoutEvent = event => {
      if (event.data?.type === 'checkoutpage.event') {
        if (event.data.payload.event === 'checkout') {
          setRefreshKey(refreshKey + 1);
        }
      }
    };

    window.addEventListener('message', handleCheckoutEvent);

    return () => {
      window.removeEventListener('message', handleCheckoutEvent);
    };
  }, [refreshKey]);

  return (
    <div className="w-full">
      <div className="my-4">
        <TestCard />
      </div>

      <div className="flex justify-between items-end mb-4">
        <div>
          <CardHeaderTitle variant="root">
            {page.type === PAGE_TYPES.EVENT ? 'Test bookings' : 'Test payments'}
          </CardHeaderTitle>
        </div>
        {page.type === PAGE_TYPES.EVENT && (
          <Button appearance="secondary" size="small" target="_blank" href={eventAppHref}>
            <CheckIcon className="w-3.5 h-3.5 mr-1.5 align-baseline" />
            Test check in guests
          </Button>
        )}
      </div>

      <div className="overflow-auto mb-4">
        {page.type === PAGE_TYPES.CHECKOUT && (
          <PaymentsChargesList
            options={{
              hidePaymentLink: true,
              hideStatus: true,
              hideAbandonment: true,
              appearance: 'compact',
            }}
            sort={{ createdAt: -1 }}
            filter={{ livemode: false, page: page._id }}
            emptyStateSubtitle="Test payments will be shown here."
            refreshKey={refreshKey}
          />
        )}

        {page.type === PAGE_TYPES.EVENT && (
          <PaymentsChargesList
            options={{
              hidePaymentLink: true,
              hideStatus: true,
              hideAbandonment: true,
              appearance: 'compact',
            }}
            sort={{ createdAt: -1 }}
            filter={{ livemode: false, page: page._id }}
            emptyStateSubtitle="Test bookings will be shown here."
            refreshKey={refreshKey}
          />
        )}
      </div>
    </div>
  );
}

function TestSubscriptions({ page, hideTestCard }) {
  const [refreshKey, setRefreshKey] = useState(0);

  // listen to postmessage checkout event
  React.useEffect(() => {
    const handleCheckoutEvent = event => {
      if (event.data?.type === 'checkoutpage.event') {
        if (event.data.payload.event === 'checkout') {
          setRefreshKey(refreshKey + 1);
        }
      }
    };

    window.addEventListener('message', handleCheckoutEvent);

    return () => {
      window.removeEventListener('message', handleCheckoutEvent);
    };
  }, [refreshKey]);

  return (
    <div className="w-full">
      {!hideTestCard && (
        <>
          <div className="my-4">
            <TestCard />
          </div>
        </>
      )}

      <CardHeaderTitle variant="root" className="mb-4">
        Test subscriptions
      </CardHeaderTitle>

      <div className="overflow-auto mb-4">
        {page.type === 'checkout' && (
          <PaymentsSubscriptionsList
            options={{
              hidePaymentLink: true,
              hideStatus: true,
              appearance: 'compact',
            }}
            sort={{ createdAt: -1 }}
            filter={{ livemode: false, page: page._id }}
            refreshKey={refreshKey}
          />
        )}
      </div>
    </div>
  );
}

export default PageTest;
